import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMaterials } from 'hooks';
import Loading from 'components/Loading';
import { colors, fonts } from 'theme';
import CreateButton from 'components/Buttons/CreateButton';
import MaterialForm from 'components/Forms/MaterialForm';
import { Event, MaterialFormInterface, UserRole } from 'types';
import { notify } from 'components/Notifier';
import { AppContext } from 'context';
import client from 'services/client';
import { MaterialFormSchema } from 'components/Forms/MaterialForm.yup';
import { DEFAULT_ITEMS_PER_SELECT } from '../../../tableFilterContext';
import MaterialLine from './MaterialLine';

const useStyles = makeStyles({
  tablePaper: {
    flex: 1,
    display: 'flex',
    margin: '2em 0 0px 0',
    padding: '0 21px 0 21px',
  },
  table: {
    marginTop: '15px',
    borderCollapse: 'separate',
    borderSpacing: '0px 10px',
  },
  headerRow: {
    '& th': {
      fontFamily: fonts.secondary,
      fontWeight: 600,
      padding: '15px 0',
      border: 'none',
      '&:first-child': {
        paddingLeft: '44px',
      },
    },
  },
  rowStyle: {
    backgroundColor: colors.white,
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    marginBottom: '10px',
    borderLeft: `10px solid ${colors.primary}`,
    height: '50px',
    '& div': {
      padding: '0 15px',
      borderBottom: 'none',
      fontFamily: fonts.secondary,
      '&:first-child': {
        paddingLeft: '34px',
      },
    },
  },
  headerStyle: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    marginBottom: '10px',
    height: '50px',
    fontWeight: 600,
    '& div': {
      padding: '0 5px',
      borderBottom: 'none',
      fontFamily: fonts.secondary,
      '&:first-child': {
        paddingLeft: '27px',
        marginRight: '17px',
      },
    },
  },
  cellStyle20Header: {
    width: '20%',
    padding: '1px 0',
  },
  cellStyle15Header: {
    width: '15%',
    padding: '1px 0',
    textTransform: 'capitalize',
  },
  cellStyle10Header: {
    width: '10%',
    padding: '10px 0',
  },
  cellStyle20: {
    width: '20%',
    padding: '0 15px',
  },
  cellStyle15: {
    width: '15%',
    padding: '0 26px',
    textTransform: 'capitalize',
  },
  cellActions: {
    borderLeft: `1px solid ${colors.greyLighten20}`,
    lineHeight: '45px',
    display: 'inline',
    verticalAlign: 'middle',
    width: '10%',
    height: '40%',
  },
  averageWeightColumn: {
    width: '10%',
  },
});

const MaterialList: React.FC = () => {
  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();
  const classes = useStyles();
  const materialList = useMaterials({
    itemsPerPage: String(DEFAULT_ITEMS_PER_SELECT),
    hasCharacterizationSubMaterial: 'true',
  });
  const roles = context.getRoles?.();
  const allowedCreate =
    sortingCenter?.isActive && roles && (context.hasLeastOneAdminRole() || roles.includes(UserRole.ROLE_MANAGER));
  const allowedUpdate =
    sortingCenter?.isActive && roles && (context.hasLeastOneAdminRole() || roles.includes(UserRole.ROLE_MANAGER));

  const { t: pt } = useTranslation('translation', { keyPrefix: 'materials' });

  const [isEditing, setEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const schema = useMemo(() => MaterialFormSchema(pt), [pt]);
  const { control, register, handleSubmit, reset } = useForm<MaterialFormInterface>({
    mode: 'onBlur',
    defaultValues: {},
    resolver: yupResolver(schema),
  });
  const onSubmit: SubmitHandler<MaterialFormInterface> = async data => {
    setIsSubmitting(true);
    try {
      if (null === data) {
        return;
      }
      if (data.averageWeight) {
        data.averageWeight = Math.round(data.averageWeight * 1000); // kg to T
      }
      await client.post('/materials', data);
      PubSub.publish(Event.REFRESH_MATERIAL);
      notify({
        message: pt('form.creation.success'),
        severity: 'success',
      });
      reset();
    } catch (e) {
      notify({
        message: pt('form.creation.error'),
        severity: 'error',
      });
    }
    setEditing(false);
    setIsSubmitting(false);
  };

  return (
    <div>
      <div className={classes.headerStyle}>
        <div className={classes.cellStyle20Header}>{pt('shortShortName')}</div>
        <div className={classes.cellStyle15Header}>{pt('designation')}</div>
        <div className={classes.cellStyle20Header}>{pt('standard')}</div>
        <div className={classes.cellStyle15Header}>{pt('conditioning')}</div>
        <div className={classes.cellStyle10Header}>{pt('averageBallWeight')}</div>
        <div className={classes.cellStyle10Header}></div>
      </div>
      <form id="MaterialForm" onSubmit={handleSubmit(onSubmit)}>
        {isEditing && (
          <MaterialForm
            control={control}
            register={register}
            onCancel={() => {
              setEditing(false);
              reset();
            }}
            isSubmitting={isSubmitting}
          />
        )}
      </form>
      <div>
        {materialList ? (
          materialList.map(material => <MaterialLine material={material} canUpdate={allowedUpdate} />)
        ) : (
          <Loading />
        )}
      </div>
      {allowedCreate && (
        <CreateButton
          disabled={isEditing}
          onClick={() => {
            setEditing(true);
          }}
          icon={null}
          label={pt('pageContent.createButtonLabel')}
        />
      )}
    </div>
  );
};
export default MaterialList;
